import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import Input from "../../../components/elements/form/input";
import Button from '../../../components/elements/button';
import { useSagesApi } from '../../../store/api/hooks/sageApiHook';
import toast from 'react-hot-toast';
import { useAppLoader } from '../../../_common/pageLoader/appLoaderHook';
import SelectBox from '../../../components/elements/form/selectBox';
import { useAssetsApi } from '../../../store/api/hooks/assetsApiHook';

interface AssetsInventoryFormProps {
    _ref?: any;
    editData?: any;
    onCreate?: () => void;
    onCancel?: () => void;
}

type AssetInventoryFormValueType = {
    inventoryDataMapID?: any;
    inventoryCategoryID?: any;
    accountGroupID?: any;
    accountID?: any;
    itemGLGroupID?: any;
};

const AddAssetsInventoryDataMapping: React.FC<AssetsInventoryFormProps> = ({ _ref, editData, onCreate = () => { }, onCancel = () => { } }) => {
    const { control, handleSubmit, setValue, reset, formState: { errors }, watch } = useForm<AssetInventoryFormValueType>({
        defaultValues: {}
    });

    const sageApi: any = useSagesApi();
    const assetsAPI = useAssetsApi();
    const { showLoader, hideLoader } = useAppLoader();

    const [assetCatOption, setassetCatOption] = useState<any>(null);
    const [accGroupOption, setaccGroupOption] = useState<any>(null);
    const [accOption, setaccOption] = useState<any>(null);
    const [itemGLGroupOption, setitemGLGroupOption] = useState<any>(null);

    const selectedAccountGroup = watch('accountGroupID'); // 👈 watch accountGroupID

    const formReset = () => {
        reset({});
    };

    const onSubmit: SubmitHandler<AssetInventoryFormValueType> = formData => {
        showLoader();
        const payload: any = {
            inventoryCategoryID: formData.inventoryCategoryID.value,
            accountGroupID: formData.accountGroupID.value,
            accountID: formData.accountID.value,
            itemGLGroupID: formData.itemGLGroupID?.value || 0,
        };

        if (editData) {
            payload['inventoryDataMapID'] = editData.inventoryDataMapID;
        }

        sageApi.saveAssetInventoryMapping(payload, (resp: any) => {
            if (resp.successful) {
                onCreate();
                onCancel();
                formReset();
                hideLoader();
                toast.success(resp?.message);
            } else {
                hideLoader();
                toast.error(resp.message || "Connection Failed");
            }
        }, (error: any) => {
            hideLoader();
            toast.error(error);
        });
    };

    const getInventoryCatList = () => {
        assetsAPI.getAssetCategpry({ pageSize: 0 }, (resp: any) => {
            const options = resp.assetCategoryList?.map((acc: any) => ({
                label: acc.categoryName,
                value: acc.categoryID
            })) || [];
            setassetCatOption(options);
        }, toast.error);
    };

    const getAccountGrouplist = () => {
        sageApi.getSyncAccountGroups({ pageSize: 0 }, (resp: any) => {
            const options = resp.sageAccountGroupList?.map((group: any) => ({
                label: group.accountGroupName,
                value: group.accountGroupID,
            })) || [];
            setaccGroupOption(options);
        }, toast.error);
    };

    const getAccountList = (accountGroupID?: number) => {
        if (!accountGroupID) {
            setaccOption([]);
            return;
        }
        sageApi.getSyncAccount({ pageSize: 0, accountGroupID }, (resp: any) => {
            const options = resp.sageAccountList?.map((acc: any) => ({
                label: acc.accountTitle,
                value: acc.accountID
            })) || [];
            setaccOption(options);
        }, toast.error);
    };

    const getItemGLList = () => {
        sageApi.getSyncItemGL({ pageSize: 0 }, (resp: any) => {
            const options = resp.sageItemGLList?.map((acc: any) => ({
                label: acc.accountTitle,
                value: acc.accountID
            })) || [];
            setitemGLGroupOption(options);
        }, toast.error);
    };

    // 👉 Update account options on accountGroupID change
    useEffect(() => {
        if (selectedAccountGroup?.value) {
            setValue('accountID', null); // clear previous account selection
            getAccountList(selectedAccountGroup.value);
        } else {
            setaccOption([]);
        }
    }, [selectedAccountGroup]);

    useEffect(() => {
        getInventoryCatList();
        getAccountGrouplist();
        getItemGLList();
    }, []);

    useEffect(() => {
        if (_ref?.current) {
            _ref.current.reset = formReset;
        }
    }, [_ref]);

    useEffect(() => {
        if (editData == null) {
            formReset();
        }
    }, [editData]);

    useEffect(() => {
        if (editData && assetCatOption && accGroupOption && accOption && itemGLGroupOption) {
            const findOption = (options: any[], id: any) => options?.find(opt => opt.value === id) || null;

            setValue('inventoryCategoryID', findOption(assetCatOption, editData.inventoryCategoryID));
            setValue('accountGroupID', findOption(accGroupOption, editData.accountGroupID));
            setValue('accountID', findOption(accOption, editData.accountID));
            setValue('itemGLGroupID', findOption(itemGLGroupOption, editData.itemGLGroupID));
        }
    }, [editData, assetCatOption, accGroupOption, accOption, itemGLGroupOption]);

    return (
        <form onSubmit={handleSubmit(onSubmit)} ref={_ref} className="space-y-5">
            <Controller
                name="inventoryCategoryID"
                control={control}
                render={({ field: { value, onChange } }) => (
                    <SelectBox
                        label='Select Inventory Category'
                        value={value}
                        onChange={onChange}
                        placeholder='Choose Inventory Category'
                        options={assetCatOption}
                        error={errors?.inventoryCategoryID?.message as any}
                    />
                )}
            />
            <Controller
                name="accountGroupID"
                control={control}
                render={({ field: { value, onChange } }) => (
                    <SelectBox
                        label='Account Group'
                        value={value}
                        onChange={onChange}
                        placeholder='Choose Account Group'
                        options={accGroupOption}
                        error={errors?.accountGroupID?.message as any}
                    />
                )}
            />
            <Controller
                name="accountID"
                control={control}
                render={({ field: { value, onChange } }) => (
                    <SelectBox
                        label='Account'
                        value={value}
                        onChange={onChange}
                        placeholder='Choose Account'
                        options={accOption}
                        error={errors?.accountID?.message as any}
                    />
                )}
            />
            {/* <Controller
                name="itemGLGroupID"
                control={control}
                render={({ field: { value, onChange } }) => (
                    <SelectBox
                        label='Item GL'
                        value={value}
                        onChange={onChange}
                        placeholder='Choose Item GL'
                        options={itemGLGroupOption}
                    />
                )}
            /> */}
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                <Button type="button" theme="secondary" onClick={formReset}>
                    Reset
                </Button>
                <Button type="submit">Save</Button>
            </div>
        </form>
    );
};

export default AddAssetsInventoryDataMapping;